/**
 * This file exposes frequently used mobile bridge methods.
 *
 * These methods are auto-imported in components and pages and can be used
 * directly in templates, <script setup> or anywhere in the Options API without
 * having to manually import them.
 */

import type {
  MobileAppMessage,
  SignupBridgeMessageInput,
} from '~/types/mobile-bridge'

/**
 * Posts a message to the mobile apps.
 *
 * @param message The message to post to the mobile apps.
 *
 * @example postMessageToMobileApp({ android: { func: 'trackSegmentEvent', params: ['test', 'test'] }, ios: { command: 'trackSegmentEvent', name: 'test', parameters: { test: 'test' } } })
 */
export function postMessageToMobileApp({ android, ios }: MobileAppMessage) {
  if (window?.AnalyticsWebInterface) {
    const params = android.params || []

    window.AnalyticsWebInterface[android.func]?.(...params)
  }
  else if (window?.webkit && window?.webkit?.messageHandlers && window?.webkit?.messageHandlers?.firebase) {
    window.webkit.messageHandlers.firebase.postMessage(ios)
  }
}

/**
 * Asks the mobile apps to open the inbox.
 *
 * @param threadId The thread ID to open the inbox.
 *
 * @example messageAppToOpenInbox('123')
 */
export function messageAppToOpenInbox(threadId: string) {
  postMessageToMobileApp({
    android: { func: 'navigateToInbox', params: [threadId] },
    ios: {
      command: 'navigateToInbox',
      parameters: {
        threadId,
      },
    },
  })
}

/**
 * Asks the mobile apps to open the payment page.
 *
 * @param bookingId The booking ID to open the payment page.
 * @param stepperCount The stepper count to open the payment page.
 *
 * @example messageAppToOpenPaymentPage(123)
 * @example messageAppToOpenPaymentPage(123, 1)
 */
export function messageAppToOpenPaymentPage(bookingId: number, stepperCount?: number) {
  const androidParams = [bookingId]
  const iosParams: Record<string, string> = {
    bookingId: bookingId.toString(),
    stepperCount: stepperCount?.toString() ?? '',
  }

  if (stepperCount) {
    androidParams.push(stepperCount)
  }

  if (!stepperCount) {
    delete iosParams.stepperCount
  }

  postMessageToMobileApp({
    android: { func: 'openPaymentPage', params: androidParams },
    ios: {
      command: 'openPaymentPage',
      parameters: iosParams,
    },
  })
}

/**
 * Asks the mobile apps to open the signup page.
 *
 * @param message The message to post to the mobile apps.
 * @param messenger The messenger to use
 * @returns A promise that resolves when the message is posted to the mobile apps
 *
 * @example messageAppToSignup({ successUrl: 'https://success.com', failureUrl: 'https://failure.com', cookies: { cookie: 'value' } })
 * @example messageAppToSignup({ successUrl: 'https://success.com', failureUrl: 'https://failure.com', cookies: { cookie: 'value' }, ownerName: 'John Doe' })
 * @example messageAppToSignup({ successUrl: 'https://success.com', failureUrl: 'https://failure.com', cookies: { cookie: 'value' }, action: 'Enquiry' })
 */
export function messageAppToSignup(
  {
    successUrl,
    failureUrl,
    cookies,
    ownerName = null,
    action = 'BookingRequest',
  }: SignupBridgeMessageInput,
  messenger = postMessageToMobileApp,
) {
  const extraJson = {
    callToAction: action,
    ...(ownerName && { name: ownerName }),
  }
  const payload = {
    android: { func: 'signup', params: [successUrl, failureUrl, cookies, JSON.stringify(extraJson)] },
    ios: {
      command: 'signup',
      parameters: {
        successURL: successUrl,
        failureURL: failureUrl,
        cookiesJSON: JSON.stringify(cookies),
        extraJson,
      },
    },
  }
  return messenger(payload)
}

/**
 * Asks the mobile apps to go to the dashboard.
 *
 * @param isHost Whether to go to the host dashboard.
 * @example messageAppToGoToDashboard()
 */
export function messageAppToGoToDashboard(isHost?: boolean) {
  if (isHost) {
    postMessageToMobileApp({
      android: { func: 'goToHostDashboard' },
      ios: {
        command: 'goToHostDashboard',
      },
    })
  }
  else {
    postMessageToMobileApp({
      android: { func: 'goToDashboard' },
      ios: {
        command: 'goToDashboard',
      },
    })
  }
}
